import { ref, onMounted, onUnmounted, nextTick } from 'vue';

/**
 * 防抖函数
 * @param {Function} func 要防抖的函数
 * @param {number} wait 等待时间
 * @returns {Function} 防抖后的函数
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * 动态计算表格滚动高度的自定义hook
 * @param {Object} options 配置选项
 * @param {string} options.selector 表格容器的CSS选择器，默认为'.menu-list-wrapper'
 * @param {number} options.minHeight 最小高度，默认为200
 * @param {number} options.maxHeight 最大高度，默认为800
 * @param {number} options.bottomSpace 底部预留空间，默认为150
 * @param {number} options.defaultHeight 默认高度，默认为350
 * @param {number} options.debounceDelay 防抖延迟时间，默认为100ms
 * @param {boolean} options.autoResize 是否自动监听窗口大小变化，默认为true
 * @returns {Object} 返回响应式的滚动高度和计算函数
 */
export function useTableScrollHeight(options = {}) {
  const {
    selector = '.menu-list-wrapper',
    minHeight = 200,
    maxHeight = 800,
    bottomSpace = 150,
    defaultHeight = 350,
    debounceDelay = 100,
    autoResize = true
  } = options;

  const tableScrollHeight = ref(defaultHeight);
  const isCalculating = ref(false);

  // 计算表格滚动高度
  const calculateTableScrollHeight = () => {
    if (isCalculating.value) return;
    
    isCalculating.value = true;
    
    try {
      const tableElement = document.querySelector(selector);
      if (tableElement) {
        // 获取视口高度
        const viewportHeight = window.innerHeight;
        // 获取表格容器距离顶部的距离
        const tableTop = tableElement.getBoundingClientRect().top;
        // 计算可用的滚动高度
        const availableHeight = viewportHeight - tableTop - bottomSpace;
        // 设置最小高度和最大高度限制
        tableScrollHeight.value = Math.max(minHeight, Math.min(maxHeight, availableHeight));
      }
    } catch (error) {
      console.warn('计算表格滚动高度时出错:', error);
    } finally {
      isCalculating.value = false;
    }
  };

  // 防抖后的计算函数
  const debouncedCalculate = debounce(calculateTableScrollHeight, debounceDelay);

  // 窗口大小变化时重新计算
  const handleResize = () => {
    if (autoResize) {
      debouncedCalculate();
    }
  };

  // 手动触发重新计算（用于某些特殊情况）
  const refreshHeight = () => {
    nextTick(() => {
      calculateTableScrollHeight();
    });
  };

  // 设置自定义高度
  const setCustomHeight = (height) => {
    tableScrollHeight.value = Math.max(minHeight, Math.min(maxHeight, height));
  };

  // 获取当前高度信息
  const getHeightInfo = () => {
    return {
      current: tableScrollHeight.value,
      min: minHeight,
      max: maxHeight,
      bottomSpace,
      isCalculating: isCalculating.value
    };
  };

  // 重置为默认高度
  const resetToDefault = () => {
    tableScrollHeight.value = defaultHeight;
  };

  // 动态更新配置
  const updateConfig = (newOptions) => {
    Object.assign(options, newOptions);
    // 重新计算高度
    nextTick(() => {
      calculateTableScrollHeight();
    });
  };

  // 监听元素可见性变化
  const observeVisibility = () => {
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // 元素可见时重新计算高度
            nextTick(() => {
              calculateTableScrollHeight();
            });
          }
        });
      });
      
      const tableElement = document.querySelector(selector);
      if (tableElement) {
        observer.observe(tableElement);
      }
      
      return observer;
    }
    return null;
  };

  let resizeObserver = null;
  let intersectionObserver = null;

  onMounted(() => {
    // 等待DOM渲染完成后计算高度
    nextTick(() => {
      calculateTableScrollHeight();
    });
    
    // 监听窗口大小变化
    if (autoResize) {
      window.addEventListener('resize', handleResize);
    }
    
    // 监听元素可见性变化
    intersectionObserver = observeVisibility();
  });

  onUnmounted(() => {
    // 移除事件监听器
    if (autoResize) {
      window.removeEventListener('resize', handleResize);
    }
    
    // 清理观察器
    if (intersectionObserver) {
      intersectionObserver.disconnect();
    }
  });

  return {
    // 响应式数据
    tableScrollHeight,
    isCalculating,
    
    // 方法
    calculateTableScrollHeight,
    refreshHeight,
    setCustomHeight,
    getHeightInfo,
    resetToDefault,
    updateConfig,
    
    // 配置信息
    config: options
  };
} 